import * as React from 'react';
import { AppComponentProps } from '../../typescriptHelpers';
import Text from 'antd/lib/typography/Text';
import { displayTextForDayOfTheWeek, displayTextForReleaseScheduleCadenceStyle } from '../../helpers/displayHelpers';
import { useForm, useWatch } from 'antd/lib/form/Form';
import FormList from 'antd/lib/form/FormList';
import FormItem from 'antd/lib/form/FormItem';
import Card from 'antd/lib/card/Card';
import Select from 'antd/lib/select';
import {
    AllDaysOfTheWeek,
    AllReleaseScheduleCadenceStyles,
    AppStoreType,
    LifecycleFreezeSet,
    CadenceTargetDateSet,
    ReleaseScheduleCadenceStyle,
} from '../../types';
import Divider from 'antd/lib/divider';
import Input from 'antd/lib/input';
import TimePicker from 'antd/lib/time-picker';
import Space from 'antd/lib/space';
import Layout, { Footer } from 'antd/lib/layout/layout';
import { antGrey, runwayColors } from '../styles/globalStyles';
import { CloseOutlined, InfoCircleOutlined, PlusOutlined } from '@ant-design/icons';
import styles from '../styles/stepStyles';
import { AppSettingsUpdatable } from '../../actions/updatable';
import { ReleaseScheduleCadenceSettingsRequest, tryUpdateAppSettings } from '../../actions/appSettings';
import { useDispatch, useSelector } from 'react-redux';
import { dayjs } from '@/extendedDayjs';
import Tooltip from 'antd/lib/tooltip';
import PermissionedActionButton from '../HOCs/PermissionedActionButton';
import { RunwayUserAction } from '../../helpers/aclHelpers';
import { globalMessage } from '../../utils/globalMessage';
import { selectAppStoreType } from '../../selectors/selectors';
import { RootState } from '../../reducers/reducers';
import { ExtendedForm } from '@/utils/ExtendedForm';
import { useExtendedForm } from '@/utils/hooks/useExtendedForm';
import { useApp, useAppSettings, useError, useIsLoading, useUpdated } from '@/utils/hooks/helpers';
import Button from 'antd/lib/button';
import { sortLifecycleFreezeSets, weekdayNumberFromDayOfWeek } from '@/helpers';
import { DatePicker } from 'antd/lib';

type ReleaseScheduleFormValues = {
    scheduleCadence: ReleaseScheduleCadenceStyle;
    cadenceTimezone: string | null;
    cadenceTargetSets: Array<{
        kickoffDayOfWeek: string | null;
        kickoffTime: dayjs.Dayjs | null;
        submitDayOfWeek: string | null;
        submitTime: dayjs.Dayjs | null;
        releaseDayOfWeek: string | null;
        releaseTime: dayjs.Dayjs | null;
    }>;
    lifecycleFreezes: Array<{
        lifecycleFreezeSetRange: [dayjs.Dayjs | null, dayjs.Dayjs | null];
    }>;
};

const ReleaseScheduleSettings: React.FC<AppComponentProps> = (props: AppComponentProps) => {
    const appSettings = useAppSettings(props);
    const appStoreType = useSelector((state: RootState) => selectAppStoreType(state, props.appId));

    const app = useApp(props);
    const scheduleSettings = appSettings.releaseScheduleCadenceSettings;
    const dispatch = useDispatch();
    const [form] = useForm();
    const [releaseScheduleFormState, releaseScheduleExtendedForm] = useExtendedForm<ReleaseScheduleFormValues>();
    const isAppleAppStore = appStoreType == AppStoreType.Apple;

    const usesStaticReleaseBranchForNonHotfixes = app.appSettings.usesStaticReleaseBranchForNonHotfixes;

    const kickoffDescription =
        usesStaticReleaseBranchForNonHotfixes ?
            'promoting the code in your working branch to your release branch'
        :   'creating a release branch';

    const defaultCadenceTargetSet = {
        kickoffDayOfWeek: null,
        kickoffTime: null, // am UTC
        submitDayOfWeek: null,
        submitTime: null,
        releaseDayOfWeek: null,
        releaseTime: null,
    };

    const defaultLifecycleFreezeSet = {
        startDate: null,
        endDate: null,
    };

    const formInitialValues = React.useMemo(() => {
        const lifecycleFreezes =
            appSettings.lifecycleFreezes?.map(({ startDate, endDate }) => {
                const parsedStartDate = dayjs(startDate).local();
                const parsedEndDate = dayjs(endDate).local();
                return {
                    lifecycleFreezeSetRange: [parsedStartDate, parsedEndDate],
                };
            }) || [];

        if (scheduleSettings) {
            const cadenceTargetSets = scheduleSettings.cadenceTargetDateSets;

            const initialValues = {
                scheduleCadence: scheduleSettings.scheduleCadence,
                cadenceTimezone: scheduleSettings.cadenceTimezone ?? null,
                cadenceTargetSets:
                    cadenceTargetSets && cadenceTargetSets.length > 0 ?
                        cadenceTargetSets.map((dateSet) => {
                            return {
                                kickoffDayOfWeek: dateSet.kickoffDayOfWeek ? dateSet.kickoffDayOfWeek : null,
                                kickoffTime:
                                    dateSet.kickoffTimeHour != null ?
                                        dayjs(`${dateSet.kickoffTimeHour}:${dateSet.kickoffTimeMinute ?? 0}`, 'hh:mm')
                                    :   null,
                                submitDayOfWeek: dateSet.submitDayOfWeek ? dateSet.submitDayOfWeek : null,
                                submitTime:
                                    dateSet.submitTimeHour !== null ?
                                        dayjs(`${dateSet.submitTimeHour}:${dateSet.submitTimeMinute ?? 0}`, 'hh:mm')
                                    :   null,
                                releaseDayOfWeek: dateSet.releaseDayOfWeek ? dateSet.releaseDayOfWeek : null,
                                releaseTime:
                                    dateSet.releaseTimeHour !== null ?
                                        dayjs(`${dateSet.releaseTimeHour}:${dateSet.releaseTimeMinute ?? 0}`, 'hh:mm')
                                    :   null,
                            };
                        })
                    :   [{ ...defaultCadenceTargetSet }],
                lifecycleFreezes,
            };

            return initialValues;
        } else {
            const initialValues = {
                scheduleCadence: ReleaseScheduleCadenceStyle.Never,
                lifecycleFreezes,
                cadenceTargetSets: [{ ...defaultCadenceTargetSet }],
                cadenceTimezone: dayjs.tz.guess(),
            };

            return initialValues;
        }
    }, [scheduleSettings]);

    const handleAddSet = () => {
        const currentDateSets = form.getFieldValue('cadenceTargetSets');
        const newValue = [...currentDateSets, { ...defaultCadenceTargetSet }];
        form.setFieldValue('cadenceTargetSets', newValue);
    };

    const handleAddLifecycleFreezeSet = () => {
        const currentLifecycleFreezeSets = form.getFieldValue('lifecycleFreezes');
        const newValue = [...currentLifecycleFreezeSets, { ...defaultLifecycleFreezeSet }];
        form.setFieldValue('lifecycleFreezes', newValue);
    };

    const cadenceTZVal = useWatch('cadenceTimezone', { form: form, preserve: true });
    const isCadenceTimezoneDirty = React.useMemo(() => {
        const initialTimezone = formInitialValues.cadenceTimezone ?? null;
        const currentTimezone = cadenceTZVal ?? null;
        return initialTimezone !== currentTimezone;
    }, [cadenceTZVal, formInitialValues.cadenceTimezone]);

    const cadenceTypeVal: ReleaseScheduleCadenceStyle = useWatch('scheduleCadence', { form: form });
    const cadenceSetsVal: ReleaseScheduleCadenceStyle = useWatch('cadenceTargetSets', { form: form });
    if (!releaseScheduleExtendedForm.form) {
        releaseScheduleExtendedForm.form = form;
    }

    const onNext = (values) => {
        form.validateFields().then(() => {
            const cadenceTargetDateSets: CadenceTargetDateSet[] =
                values.cadenceTargetSets ?
                    values.cadenceTargetSets.map((dateSet) => {
                        return {
                            kickoffDayOfWeek: dateSet.kickoffDayOfWeek,
                            kickoffTimeHour: dateSet.kickoffTime ? (dateSet.kickoffTime as dayjs.Dayjs).hour() : null,
                            kickoffTimeMinute:
                                dateSet.kickoffTime ? (dateSet.kickoffTime as dayjs.Dayjs).minute() : null,
                            submitDayOfWeek: dateSet.submitDayOfWeek,
                            submitTimeHour: dateSet.submitTime ? (dateSet.submitTime as dayjs.Dayjs).hour() : null,
                            submitTimeMinute: dateSet.submitTime ? (dateSet.submitTime as dayjs.Dayjs).minute() : null,
                            releaseDayOfWeek: dateSet.releaseDayOfWeek,
                            releaseTimeHour: dateSet.releaseTime ? (dateSet.releaseTime as dayjs.Dayjs).hour() : null,
                            releaseTimeMinute:
                                dateSet.releaseTime ? (dateSet.releaseTime as dayjs.Dayjs).minute() : null,
                        };
                    })
                :   [];
            const lifecycleFreezes: LifecycleFreezeSet[] = values.lifecycleFreezes.map((dateSet) => {
                const [start, end] = dateSet.lifecycleFreezeSetRange;
                return {
                    startDate: start.format('YYYY-MM-DD'),
                    endDate: end.format('YYYY-MM-DD'),
                };
            });

            const request: ReleaseScheduleCadenceSettingsRequest = {
                orgId: props.orgId,
                appId: props.appId,
                scheduleCadence: values.scheduleCadence,
                cadenceTargetDateSets: cadenceTargetDateSets,
                lifecycleFreezes: sortLifecycleFreezeSets(lifecycleFreezes),
                cadenceTimezone: cadenceTZVal,
            };

            dispatch(tryUpdateAppSettings(AppSettingsUpdatable.UpdateReleaseScheduleCadenceSettings, request));
        });
    };

    const updateCadenceIsLoading = useIsLoading(AppSettingsUpdatable.UpdateReleaseScheduleCadenceSettings);
    const updateCadenceError = useError(AppSettingsUpdatable.UpdateReleaseScheduleCadenceSettings);
    const updateCadenceSuccess = useUpdated(AppSettingsUpdatable.UpdateReleaseScheduleCadenceSettings);

    React.useEffect(() => {
        if (updateCadenceError) {
            globalMessage.error(updateCadenceError);
        }
    }, [updateCadenceError]);

    React.useEffect(() => {
        if (updateCadenceSuccess) {
            globalMessage.success('Release schedule settings updated!');
        }
    }, [updateCadenceSuccess]);

    React.useEffect(() => {
        if (
            cadenceTypeVal &&
            !(
                cadenceTypeVal === ReleaseScheduleCadenceStyle.Weekly ||
                cadenceTypeVal === ReleaseScheduleCadenceStyle.Never
            )
        ) {
            const currentSets = form.getFieldValue('cadenceTargetSets');
            const newVal = currentSets?.length > 0 ? [{ ...currentSets[0] }] : [{ ...defaultCadenceTargetSet }];
            form.setFieldValue('cadenceTargetSets', newVal);
        }
    }, [cadenceTypeVal]);

    const compactFormItemStyle: React.CSSProperties = {
        width: '30%',
    };

    const timeInputStyle: React.CSSProperties = {
        width: '100%',
    };

    const selectInputStyle: React.CSSProperties = {
        width: 100,
    };

    const inputGroupStyle: React.CSSProperties = {
        marginTop: 16,
        width: '50%',
    };

    return (
        <Layout>
            <ExtendedForm
                initialValues={formInitialValues}
                extendedFormObject={releaseScheduleExtendedForm}
                onFinish={onNext}
                onFinishFailed={() =>
                    globalMessage.error('Cadence form is invalid; please address problems, then resubmit.')
                }
                layout="vertical"
                preserve
            >
                <FormItem
                    label="Release schedule cadence"
                    name="scheduleCadence"
                    rules={[{ required: true }]}
                    style={{ width: '40%', minWidth: 300, maxWidth: 400 }}
                >
                    <Select placeholder="Select">
                        {AllReleaseScheduleCadenceStyles.map((cadenceStyle, index) => {
                            return (
                                <Select.Option value={cadenceStyle} key={index}>
                                    {displayTextForReleaseScheduleCadenceStyle(cadenceStyle)}
                                </Select.Option>
                            );
                        })}
                    </Select>
                </FormItem>
                <FormItem label="Time zone" style={{ width: '40%', minWidth: 300, maxWidth: 400 }} shouldUpdate>
                    <div style={{ minWidth: 150 }}>
                        <Select
                            placeholder="Select"
                            showSearch
                            disabled={cadenceTypeVal === ReleaseScheduleCadenceStyle.Never}
                            allowClear
                            value={cadenceTZVal}
                            onSelect={(value) => {
                                form.setFieldValue('cadenceTimezone', value);
                            }}
                            onClear={() => {
                                form.setFieldValue('cadenceTimezone', null);
                            }}
                        >
                            {Intl.supportedValuesOf('timeZone').map((timezone, index) => {
                                return (
                                    <Select.Option value={timezone} key={index}>
                                        {timezone}
                                    </Select.Option>
                                );
                            })}
                        </Select>
                    </div>
                </FormItem>
                <Divider />
                <Text>
                    <div style={{ paddingBottom: '8px' }}>Release event dates</div>
                </Text>
                <FormList name="cadenceTargetSets">
                    {(fields, { remove }) => {
                        return (
                            <>
                                {fields.map((fieldData, i) => {
                                    return (
                                        <Card
                                            title={
                                                cadenceSetsVal?.length > 1 ?
                                                    <div
                                                        style={{
                                                            display: 'flex',
                                                            flexDirection: 'row',
                                                            alignItems: 'center',
                                                            justifyContent: 'space-between',
                                                        }}
                                                    >
                                                        <Text
                                                            style={{
                                                                width: 24,
                                                                height: 24,
                                                                borderRadius: 12,
                                                                backgroundColor: runwayColors.primary,
                                                                color: 'white',
                                                                display: 'flex',
                                                                justifyContent: 'center',
                                                                alignItems: 'center',
                                                                fontWeight: 'normal',
                                                                flexShrink: 0,
                                                            }}
                                                        >
                                                            {i + 1}
                                                        </Text>
                                                        <div style={{ visibility: i > 0 ? 'visible' : 'hidden' }}>
                                                            <Button
                                                                icon={<CloseOutlined />}
                                                                type="text"
                                                                disabled={
                                                                    i === 0 ||
                                                                    cadenceTypeVal === ReleaseScheduleCadenceStyle.Never
                                                                }
                                                                onClick={() => remove(i)}
                                                            />
                                                        </div>
                                                    </div>
                                                :   null
                                            }
                                            key={`&dateSet=${i}`}
                                            style={{ marginBottom: '15px' }}
                                        >
                                            <Space>
                                                <Text type="secondary">
                                                    Set a target date and time to kick off your release.
                                                </Text>
                                                <Tooltip
                                                    title={`If you have the auto-kickoff automation enabled, Runway will kick off your release by ${kickoffDescription}. You can turn individual automations on or off from App Settings.`}
                                                >
                                                    <InfoCircleOutlined style={{ color: antGrey.grey7 }} />
                                                </Tooltip>
                                            </Space>
                                            <Input.Group compact style={inputGroupStyle}>
                                                <FormItem
                                                    label="Kickoff day"
                                                    name={[fieldData.name, 'kickoffDayOfWeek']}
                                                    style={compactFormItemStyle}
                                                    shouldUpdate
                                                    validateTrigger="onFinish"
                                                    rules={[
                                                        {
                                                            validator: (_, value) => {
                                                                const configuredKickoffTime = form.getFieldValue([
                                                                    'cadenceTargetSets',
                                                                    i,
                                                                    'kickoffTime',
                                                                ]);
                                                                if (configuredKickoffTime && !value) {
                                                                    return Promise.reject(
                                                                        'Kickoff day is required if kickoff time is configured',
                                                                    );
                                                                } else if (cadenceSetsVal?.length > 1) {
                                                                    if (!value) {
                                                                        return Promise.reject(
                                                                            'Kickoff configuration is required to support multiple releases per week',
                                                                        );
                                                                    }

                                                                    if (i > 0) {
                                                                        const priorSetKickoffDay = form.getFieldValue([
                                                                            'cadenceTargetSets',
                                                                            i - 1,
                                                                            'kickoffDayOfWeek',
                                                                        ]);

                                                                        if (
                                                                            priorSetKickoffDay != undefined &&
                                                                            weekdayNumberFromDayOfWeek(
                                                                                priorSetKickoffDay,
                                                                            ) >= weekdayNumberFromDayOfWeek(value)
                                                                        ) {
                                                                            return Promise.reject(
                                                                                'Must be a later weekday than the kickoff day selected in the preceding date set',
                                                                            );
                                                                        }
                                                                    }
                                                                }

                                                                return Promise.resolve();
                                                            },
                                                        },
                                                    ]}
                                                >
                                                    <Select
                                                        placeholder="Select"
                                                        allowClear
                                                        disabled={cadenceTypeVal === ReleaseScheduleCadenceStyle.Never}
                                                        style={selectInputStyle}
                                                    >
                                                        {AllDaysOfTheWeek.map((dayOfTheWeek, index) => {
                                                            return (
                                                                <Select.Option value={dayOfTheWeek} key={index}>
                                                                    {displayTextForDayOfTheWeek(dayOfTheWeek)}
                                                                </Select.Option>
                                                            );
                                                        })}
                                                    </Select>
                                                </FormItem>
                                                <FormItem
                                                    label="Kickoff time"
                                                    name={[fieldData.name, 'kickoffTime']}
                                                    style={compactFormItemStyle}
                                                    shouldUpdate
                                                    validateTrigger="onFinish"
                                                    rules={[
                                                        {
                                                            validator: (_, value) => {
                                                                const configuredKickoffDay = form.getFieldValue([
                                                                    'cadenceTargetSets',
                                                                    i,
                                                                    'kickoffDayOfWeek',
                                                                ]);
                                                                if (configuredKickoffDay && !value) {
                                                                    return Promise.reject(
                                                                        'Kickoff time is required if kickoff day of the week is selected',
                                                                    );
                                                                } else if (!value && cadenceSetsVal?.length > 1) {
                                                                    return Promise.reject(
                                                                        'Kickoff configuration is required to support multiple releases per week',
                                                                    );
                                                                } else {
                                                                    return Promise.resolve();
                                                                }
                                                            },
                                                        },
                                                    ]}
                                                >
                                                    {makeReleaseScheduleTimePicker(
                                                        timeInputStyle,
                                                        cadenceTypeVal === ReleaseScheduleCadenceStyle.Never,
                                                    )}
                                                </FormItem>
                                            </Input.Group>
                                            <Space>
                                                <Text type="secondary">
                                                    Set a target date and time to submit your app for review.
                                                </Text>
                                                <Tooltip
                                                    title={`If you have the auto-submit automation enabled, Runway will submit your selected build for review if all previous steps are "Ready". You can turn individual automations on or off from App Settings.`}
                                                >
                                                    <InfoCircleOutlined style={{ color: antGrey.grey7 }} />
                                                </Tooltip>
                                            </Space>
                                            <Input.Group compact style={inputGroupStyle}>
                                                <FormItem
                                                    label="Submit day"
                                                    name={[fieldData.name, 'submitDayOfWeek']}
                                                    style={compactFormItemStyle}
                                                    validateTrigger="onFinish"
                                                    rules={[
                                                        {
                                                            validator: (_, value) => {
                                                                const configuredSubmitTime = form.getFieldValue([
                                                                    'cadenceTargetSets',
                                                                    i,
                                                                    'submitTime',
                                                                ]);
                                                                if (configuredSubmitTime && !value) {
                                                                    return Promise.reject(
                                                                        'Submit day is required if submit time is configured.',
                                                                    );
                                                                } else if (i > 0) {
                                                                    const firstSetSubmitDay = form.getFieldValue([
                                                                        'cadenceTargetSets',
                                                                        0,
                                                                        'submitDayOfWeek',
                                                                    ]);

                                                                    if (firstSetSubmitDay && !value) {
                                                                        return Promise.reject(
                                                                            'Targets must be consistently configured across sets (submit day configured in one or more preceding sets)',
                                                                        );
                                                                    } else if (!firstSetSubmitDay && value) {
                                                                        return Promise.reject(
                                                                            'Targets must be consistently configured across sets (submit day missing in one or more preceding sets)',
                                                                        );
                                                                    }
                                                                }

                                                                return Promise.resolve();
                                                            },
                                                        },
                                                    ]}
                                                >
                                                    <Select
                                                        placeholder="Select"
                                                        allowClear
                                                        disabled={cadenceTypeVal === ReleaseScheduleCadenceStyle.Never}
                                                        style={selectInputStyle}
                                                    >
                                                        {AllDaysOfTheWeek.map((dayOfTheWeek, index) => {
                                                            return (
                                                                <Select.Option value={dayOfTheWeek} key={index}>
                                                                    {displayTextForDayOfTheWeek(dayOfTheWeek)}
                                                                </Select.Option>
                                                            );
                                                        })}
                                                    </Select>
                                                </FormItem>
                                                <FormItem
                                                    label="Submit time"
                                                    name={[fieldData.name, 'submitTime']}
                                                    style={compactFormItemStyle}
                                                    shouldUpdate
                                                    validateTrigger="onFinish"
                                                    rules={[
                                                        {
                                                            validator: (_, value) => {
                                                                const configuredSubmitDay = form.getFieldValue([
                                                                    'cadenceTargetSets',
                                                                    i,
                                                                    'submitDayOfWeek',
                                                                ]);
                                                                if (configuredSubmitDay && !value) {
                                                                    return Promise.reject(
                                                                        'Submit time is required if submit day of the week is selected',
                                                                    );
                                                                } else if (i > 0) {
                                                                    const firstSetSubmitTime = form.getFieldValue([
                                                                        'cadenceTargetSets',
                                                                        0,
                                                                        'submitTime',
                                                                    ]);

                                                                    if (firstSetSubmitTime && !value) {
                                                                        return Promise.reject(
                                                                            'Targets must be consistently configured across sets (submit time configured in one or more preceding sets)',
                                                                        );
                                                                    } else if (!firstSetSubmitTime && value) {
                                                                        return Promise.reject(
                                                                            'Targets must be consistently configured across sets (submit time missing in one or more preceding sets)',
                                                                        );
                                                                    }
                                                                }

                                                                return Promise.resolve();
                                                            },
                                                        },
                                                    ]}
                                                >
                                                    {makeReleaseScheduleTimePicker(
                                                        timeInputStyle,
                                                        cadenceTypeVal === ReleaseScheduleCadenceStyle.Never,
                                                    )}
                                                </FormItem>
                                            </Input.Group>
                                            {isAppleAppStore && (
                                                <div>
                                                    <Space>
                                                        <Text type="secondary">
                                                            Set a target date and time to release your app update.
                                                        </Text>
                                                        <Tooltip
                                                            title={`If you have the auto-release automation enabled, Runway will release your approved build on your target date. You can turn individual automations on or off from App Settings.`}
                                                        >
                                                            <InfoCircleOutlined style={{ color: antGrey.grey7 }} />
                                                        </Tooltip>
                                                    </Space>
                                                    <Input.Group compact style={inputGroupStyle}>
                                                        <FormItem
                                                            label="Release day"
                                                            name={[fieldData.name, 'releaseDayOfWeek']}
                                                            style={compactFormItemStyle}
                                                            validateTrigger="onFinish"
                                                            rules={[
                                                                {
                                                                    validator: (_, value) => {
                                                                        const configuredReleaseTime =
                                                                            form.getFieldValue([
                                                                                'cadenceTargetSets',
                                                                                i,
                                                                                'releaseTime',
                                                                            ]);
                                                                        if (configuredReleaseTime && !value) {
                                                                            return Promise.reject(
                                                                                'Release day is required if release time is configured.',
                                                                            );
                                                                        } else if (i > 0) {
                                                                            const firstSetReleaseDay =
                                                                                form.getFieldValue([
                                                                                    'cadenceTargetSets',
                                                                                    0,
                                                                                    'releaseDayOfWeek',
                                                                                ]);

                                                                            if (firstSetReleaseDay && !value) {
                                                                                return Promise.reject(
                                                                                    'Targets must be consistently configured across sets (release day configured in one or more preceding sets)',
                                                                                );
                                                                            } else if (!firstSetReleaseDay && value) {
                                                                                return Promise.reject(
                                                                                    'Targets must be consistently configured across sets (release day missing in one or more preceding sets)',
                                                                                );
                                                                            }
                                                                        }

                                                                        return Promise.resolve();
                                                                    },
                                                                },
                                                            ]}
                                                        >
                                                            <Select
                                                                placeholder="Select"
                                                                disabled={
                                                                    cadenceTypeVal === ReleaseScheduleCadenceStyle.Never
                                                                }
                                                                style={selectInputStyle}
                                                                allowClear
                                                            >
                                                                {AllDaysOfTheWeek.map((dayOfTheWeek, index) => {
                                                                    return (
                                                                        <Select.Option value={dayOfTheWeek} key={index}>
                                                                            {displayTextForDayOfTheWeek(dayOfTheWeek)}
                                                                        </Select.Option>
                                                                    );
                                                                })}
                                                            </Select>
                                                        </FormItem>
                                                        <FormItem
                                                            label="Release time"
                                                            name={[fieldData.name, 'releaseTime']}
                                                            style={compactFormItemStyle}
                                                            validateTrigger="onFinish"
                                                            rules={[
                                                                {
                                                                    validator: (_, value) => {
                                                                        const configuredReleaseDay = form.getFieldValue(
                                                                            [
                                                                                'cadenceTargetSets',
                                                                                i,
                                                                                'releaseDayOfWeek',
                                                                            ],
                                                                        );
                                                                        if (configuredReleaseDay && !value) {
                                                                            return Promise.reject(
                                                                                'Release time is required if release day of the week is selected',
                                                                            );
                                                                        } else if (i > 0) {
                                                                            const firstSetReleaseTime =
                                                                                form.getFieldValue([
                                                                                    'cadenceTargetSets',
                                                                                    0,
                                                                                    'releaseTime',
                                                                                ]);

                                                                            if (firstSetReleaseTime && !value) {
                                                                                return Promise.reject(
                                                                                    'Targets must be consistently configured across sets (release time configured in one or more preceding sets)',
                                                                                );
                                                                            } else if (!firstSetReleaseTime && value) {
                                                                                return Promise.reject(
                                                                                    'Targets must be consistently configured across sets (release time missing in one or more preceding sets)',
                                                                                );
                                                                            }
                                                                        }

                                                                        return Promise.resolve();
                                                                    },
                                                                },
                                                            ]}
                                                        >
                                                            {makeReleaseScheduleTimePicker(
                                                                timeInputStyle,
                                                                cadenceTypeVal === ReleaseScheduleCadenceStyle.Never,
                                                            )}
                                                        </FormItem>
                                                    </Input.Group>
                                                </div>
                                            )}
                                        </Card>
                                    );
                                })}
                            </>
                        );
                    }}
                </FormList>
                {cadenceTypeVal === ReleaseScheduleCadenceStyle.Weekly ?
                    <Button
                        onClick={handleAddSet}
                        disabled={cadenceSetsVal?.length >= 7}
                        type="default"
                        icon={<PlusOutlined />}
                        style={{ marginRight: 16 }}
                    >
                        Add another set of release event dates
                    </Button>
                :   null}
                <div style={{ paddingBottom: 8, paddingTop: 20 }}>
                    <Space>
                        <Text>Lifecycle freeze dates</Text>
                        <Tooltip
                            title={`Runway will pause scheduled automations related to kickoff, submission, and release during the selected lifecycle freeze dates.`}
                        >
                            <InfoCircleOutlined style={{ color: antGrey.grey7 }} />
                        </Tooltip>
                    </Space>
                </div>
                <FormList name="lifecycleFreezes">
                    {(fields, { remove }) => {
                        return (
                            <>
                                {fields.map((fieldData, i) => {
                                    return (
                                        <Card
                                            title={
                                                <div
                                                    style={{
                                                        display: 'flex',
                                                        flexDirection: 'row',
                                                        alignItems: 'center',
                                                        justifyContent: 'space-between',
                                                    }}
                                                >
                                                    <Text
                                                        style={{
                                                            width: 24,
                                                            height: 24,
                                                            borderRadius: 12,
                                                            backgroundColor: runwayColors.primary,
                                                            color: 'white',
                                                            display: 'flex',
                                                            justifyContent: 'center',
                                                            alignItems: 'center',
                                                            fontWeight: 'normal',
                                                            flexShrink: 0,
                                                        }}
                                                    >
                                                        {i + 1}
                                                    </Text>
                                                    <div>
                                                        <Button
                                                            icon={<CloseOutlined />}
                                                            type="text"
                                                            onClick={() => remove(i)}
                                                        />
                                                    </div>
                                                </div>
                                            }
                                            key={`&lifecycleFreezeSet=${i}`}
                                            style={{ marginBottom: '15px' }}
                                        >
                                            <Space>
                                                <Text type="secondary">Set period to prevent release events.</Text>
                                            </Space>
                                            <Input.Group compact style={{ marginTop: 16 }}>
                                                <FormItem
                                                    name={[fieldData.name, 'lifecycleFreezeSetRange']}
                                                    shouldUpdate
                                                    validateTrigger="onFinish"
                                                    style={compactFormItemStyle}
                                                    rules={[
                                                        {
                                                            validator: (_, value) => {
                                                                if (!value || value.length !== 2) {
                                                                    return Promise.reject(
                                                                        new Error('Please select a valid date range'),
                                                                    );
                                                                }

                                                                const [start, end] = value;

                                                                if (!dayjs.isDayjs(start) || !dayjs.isDayjs(end)) {
                                                                    return Promise.reject(
                                                                        new Error('Invalid date format'),
                                                                    );
                                                                }

                                                                return Promise.resolve();
                                                            },
                                                        },
                                                    ]}
                                                >
                                                    <DatePicker.RangePicker
                                                        disabledDate={(current) =>
                                                            current && current < dayjs().endOf('day').subtract(1, 'day')
                                                        }
                                                    />
                                                </FormItem>
                                            </Input.Group>
                                        </Card>
                                    );
                                })}
                            </>
                        );
                    }}
                </FormList>

                <Button onClick={handleAddLifecycleFreezeSet} type="default" icon={<PlusOutlined />}>
                    Add lifecycle freeze dates to prevent release events
                </Button>
            </ExtendedForm>

            <Footer style={{ ...styles.footerStyle }}>
                <PermissionedActionButton
                    actionType={RunwayUserAction.UAUpdateScheduleCadence}
                    onClick={() => form.submit()}
                    loading={updateCadenceIsLoading}
                    disabled={!releaseScheduleFormState.isDirty && !isCadenceTimezoneDirty}
                    type="primary"
                >
                    Save changes
                </PermissionedActionButton>
            </Footer>
        </Layout>
    );
};

const makeReleaseScheduleTimePicker = (style: React.CSSProperties, disabled: boolean) => {
    return (
        <TimePicker
            defaultOpenValue={dayjs().set('hour', 0).set('minute', 0)}
            format="h:mm A"
            minuteStep={30}
            showNow={false}
            disabled={disabled}
            allowClear
            style={style}
        />
    );
};

export default ReleaseScheduleSettings;
